import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { useAtom } from "jotai";
import classNames from "classnames";
import { Page, Typo } from "@/model";
import typoRepository from "@/repository/typoRepository";
import { useDeleteTypo } from "@/query/typo";
import { editingTypoAtom } from "@/atom/editing";
import { useAgentId, useDialog, useSelection } from "@/hook";
import HighlightText from "@/component/HighlightText";
import { IndeterminateCheckbox } from "@/component/input";
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from "@/component/Popover";
import { errorToast, successToast } from "@/component/Toast";
import SelectionActionBar from "@/component/SelectionActionBar";
import SortButton from "@/component/SortBurtton";
import { DeleteIcon, EditIcon } from "@/assets/svgs/icons";

import { TypoPageParam } from "../TypoView";


interface TypoTableProps {
    page?: Page<Typo>;
    pageParam: TypoPageParam;
    onClickSortHead: (order: 'asc' | 'desc') => void;
}

const TypoTable: React.FC<TypoTableProps> = (props) => {
    const { page, pageParam, onClickSortHead } = props;
    
    const intl = useIntl();
    const agentId = useAgentId();
    const dialog = useDialog();

    const [editingTypo, setEditingTypo] = useAtom(editingTypoAtom);
    const { mutateAsync: deleteTypo } = useDeleteTypo(agentId);

    const pageIds = useMemo(() => page?.content.map(typo => typo.id) ?? [], [page?.content]);

    const { selectionCount, isAllSelected, pageSelectionStatus, selectedIds, markOne, markPage, markAll, clearSelection } = useSelection({
        targetIds: pageIds,
        total: page?.total ?? 0
    });

    const deleteByIds = async (ids: string[]) => {
        await deleteTypo({ ids: ids })
            .then(() => {
                if (editingTypo && ids.includes(editingTypo.id)) {
                    setEditingTypo(undefined);
                }
                clearSelection();
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const deleteAll = async () => {
        await deleteTypo({ keyword: pageParam.searchKeyword })
            .then(() => {
                setEditingTypo(undefined);
                clearSelection();
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const onClickDownload = () => {
        if (isAllSelected) {
            typoRepository.export(agentId, { keyword: pageParam.searchKeyword });
        } else {
            typoRepository.export(agentId, { ids: selectedIds });
        }
    };

    const onClickDeleteOne = (id: string) => {
        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i07010" }),
            content: intl.formatMessage({ id: "i07016" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: () => deleteByIds([id])
        });
    };

    const onClickDeleteSelection = () => {
        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i07013" }),
            content: intl.formatMessage({ id: "i07014" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: () => isAllSelected ? deleteAll() : deleteByIds(selectedIds)
        });
    };

    return (
        <div role="table"
             className="selection-table grid-table-typo"
        >
            <div role="rowgroup"
                 className="selection-table-thead"
            >
                <div role="columnheader"
                     className="selection-table-th"
                >
                    <IndeterminateCheckbox status={pageSelectionStatus}
                                           onChange={markPage}
                    />
                </div>
                <div role="columnheader"
                     className="selection-table-th"
                >
                    <SortButton order={pageParam.sortOrder}
                                onClick={() => onClickSortHead(pageParam.sortOrder === "asc" ? "desc" : "asc")}
                    >
                        {intl.formatMessage({ id: "i07011" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[15px]"
                >
                    {intl.formatMessage({ id: "i07003" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th"
                />
            </div>
            <div role="rowgroup"
                 className="selection-table-tbody"
            >
                {page?.content.map(typo => (
                    <div key={typo.id}
                         role="row"
                         className="selection-table-tr group"
                    >
                        <div role="cell"
                             className="selection-table-td"
                        >
                            <input type="checkbox"
                                    className={classNames(
                                        selectedIds.length ? "opacity-100" : "opacity-0",
                                        "selection-table-checkbox group-interaction"
                                    )}
                                    checked={selectedIds.includes(typo.id)}
                                    onChange={e => markOne(typo.id, e.target.checked)}
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td"
                        >
                            <HighlightText query={typo.keyword}
                                           keyword={pageParam.searchKeyword}
                                           highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td pl-[15px]"
                        >
                            <HighlightText query={typo.typos.join(", ")}
                                           keyword={pageParam.searchKeyword}
                                           highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td"
                        >
                            <Popover>
                                {({ open, close }) => (
                                    <>
                                        <PopoverButton className="selection-table-popover-btn"
                                                       open={open}
                                                       close={close}
                                        />
                                        <PopoverMenu>
                                            <PopoverItem className="selection-table-popover-item"
                                                        onClick={() => setEditingTypo(typo)}   
                                            >
                                                <EditIcon className="size-3.5" />
                                                {intl.formatMessage({ id: "i07009" })}
                                            </PopoverItem>
                                            <PopoverItem className="selection-table-popover-item text-point-red"
                                                        onClick={() => onClickDeleteOne(typo.id)}
                                            >
                                                <DeleteIcon className="size-3.5" />
                                                {intl.formatMessage({ id: "i07010" })}
                                            </PopoverItem>
                                        </PopoverMenu>
                                    </>
                                )}
                            </Popover>
                        </div>
                    </div>
                ))}
                {!page?.content?.length && (
                    <div className="empty-table">
                        {intl.formatMessage({ id: pageParam.searchKeyword ? "b0014" : "b0018" })}
                    </div>
                )}
            </div>

            <SelectionActionBar selectionCount={selectionCount}
                                selectAll={() => markAll(true)}
                                deSelect={() => markAll(false)}
                                remove={onClickDeleteSelection}
                                download={onClickDownload}
            />
        </div>
    );
};

export default TypoTable;
